import React, {
  Fragment,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { Route, Routes, useNavigate } from 'react-router-dom';
import Helmet from 'react-helmet';

import {
  Application,
  FieldAnswer,
  FormResponse,
  Participant,
  Recruitment,
  Session,
  StudyType,
  useApply,
  useCurrentParticipant,
  useForm,
  useSessionLink,
} from '@app/api';
import { setLocale } from '@app/i18n';

import { PublicBackground, useToast } from '@app/core';

import { SignupParticipant } from './SignupParticipant';
import {
  AnalyticsCategoryProvider,
  Categories,
  useTrackApplied,
  useTrackingParams,
  useTrackScreenerFinished,
  useFingerprint,
} from '@app/analytics';
import { AddToPanel } from './AddToPanel';
import { ParticipationRules } from './ParticipationRules';
import { StudyProfile, useProfileSteps } from './StudyProfile';
import { useBuildCreateFormResponse } from './CustomForm/hooks/useBuildCreateFormResponse';
import { UpdateProfile } from './UpdateProfile';
import { useRenderField } from './CustomForm/hooks/useRenderField';
import { ParticipantDeviceChoice } from './ParticipantDeviceChoice';
import { useAppliedRedirection } from './hooks/useAppliedRedirection';
import { useTranslation } from 'react-i18next';
import { ParticipantJobChoice } from './components/ParticipantJobChoice';

interface Props {
  session: Session;
  recruitment: Recruitment;
  participantId?: string;
  incentiveText?: string;
  sessionLinkId?: string;
  trackingId?: string;
}

export const ApplicationSteps = ({
  session,
  participantId: participantIdProp,
  incentiveText,
  recruitment,
  sessionLinkId,
  trackingId,
}: Props) => {
  const navigate = useNavigate();
  const toast = useToast();
  const { t } = useTranslation();
  const [participantIdState, setParticipantId] = useState<string | undefined>(
    participantIdProp,
  );
  const tracking = useTrackingParams();
  const { data: sessionLink } = useSessionLink(sessionLinkId);
  const redirect = useAppliedRedirection(sessionLinkId);

  const [savedApplication, setApplication] = useState<
    Application | undefined
  >();
  const criteriaRefs = useMemo(
    () => recruitment.criteria.map((c) => c.fieldRef),
    [recruitment.criteria],
  );

  const startedAt = useMemo(() => new Date(), []);
  const {
    profile,
    buildCreateFormResponse,
    setScreenerResponse,
    setProfile,
    setDeviceChoice,
    setJobChoice,
  } = useBuildCreateFormResponse({
    screenerFormId: recruitment.screeningFormId,
    startedAt,
    criteria: recruitment.criteria,
  });

  const { data: participant } = useCurrentParticipant(true);
  const { isLoading: isFormLoading, data: form } = useForm(
    recruitment.screeningFormId,
  );

  const [hasAcceptedToJoinPanel, setAcceptedToJoinPanel] = useState(false);
  const { mutateAsync: applyMutation } = useApply();
  const trackApplied = useTrackApplied();
  const trackScreener = useTrackScreenerFinished();
  const fingerprint = useFingerprint();
  const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

  const participantId =
    participantIdState || participantIdProp || participant?.id;
  const goToNextStep = (withJob = false) => {
    if (withJob) {
      navigate('job');
    } else if (profileSteps.length > 0) {
      navigate('profile');
    } else {
      navigate('personal');
    }
  };

  const goToTheFinalPage = useCallback(
    (application?: Application) => {
      const app = application || savedApplication;
      if (app) {
        redirect(app, {
          showAddToPanelInfo: hasAcceptedToJoinPanel,
        });
      } else {
        toast.exception();
      }
    },
    [redirect, savedApplication, hasAcceptedToJoinPanel, toast],
  );

  const onProfileFinished = useCallback(
    (profile: Partial<Participant>) => {
      setProfile(profile);
      navigate('personal');
    },
    [navigate, setProfile],
  );

  const onDeviceChoiceSubmit = (answer: FieldAnswer) => {
    setDeviceChoice(answer);
    goToNextStep(!recruitment.allowTechParticipants);
  };

  const onJobChoiceSubmit = (answer: FieldAnswer) => {
    setJobChoice(answer);
    goToNextStep();
  };

  const showParticipationRules = useCallback(
    (participant: Participant) => {
      setParticipantId(participant.id);
      navigate('participation-rules');
    },
    [navigate],
  );

  const allowJoinPanel = session.allowJoinPanel;
  const apply = useCallback(async () => {
    if (!participantId) {
      return;
    }
    const formResponse = buildCreateFormResponse();

    const application = await applyMutation({
      participantId,
      recruitmentId: recruitment.id,
      sessionId: session.id,
      sessionLinkId,
      tracking,
      trackingId,
      formResponse,
      fingerprint,
      timezone,
    });
    setApplication(application);
    trackApplied({
      sessionId: session.id,
      sessionLink: sessionLinkId,
      participantId,
    });
    toast.saved();

    if (
      allowJoinPanel &&
      !participant?.panelId &&
      (!sessionLink || sessionLink.recruitmentByTandemz)
    ) {
      navigate('add-to-panel');
      return;
    }
    goToTheFinalPage(application);
  }, [
    applyMutation,
    navigate,
    participant,
    participantId,
    allowJoinPanel,
    toast,
    tracking,
    buildCreateFormResponse,
    setApplication,
    sessionLinkId,
    goToTheFinalPage,
    trackingId,
  ]);

  const onAddToPanelSubmit = (joinedPanel: boolean) => {
    setAcceptedToJoinPanel(joinedPanel);
    if (joinedPanel) {
      navigate('update-profile');
    } else {
      goToTheFinalPage();
    }
  };

  const locale = session.locale;
  useEffect(() => {
    setLocale(locale);
  }, [locale]);

  const {
    currentFieldsByCategories,
    steps: profileSteps,
    currentStep: currentProfileStep,
    onSubmit: onProfileSubmit,
  } = useProfileSteps({ criteriaRefs, onFinish: onProfileFinished });

  const onFormFinished = useCallback(
    (formResponse: FormResponse) => {
      setScreenerResponse(formResponse);
      trackScreener();

      if (
        session.studyType === StudyType.MODERATED &&
        session.allowedDevicesCategories?.length
      ) {
        navigate('device');
        return;
      }

      if (!recruitment.allowTechParticipants) {
        navigate('job');
        return;
      }

      if (!profileSteps.length) {
        navigate('personal');
        return;
      }

      navigate('profile');
    },
    [
      setScreenerResponse,
      trackScreener,
      recruitment.allowTechParticipants,
      session.studyType,
      session.allowedDevicesCategories?.length,
      profileSteps.length,
      navigate,
    ],
  );

  const steps = useMemo(
    () => [
      ...(form?.fieldIds || []),
      'device',
      'job',
      ...profileSteps,
      'personal',
      'participation-rules',
      'add-to-panel',
      'update-profile',
    ],
    [form?.fieldIds, profileSteps],
  );
  const pageCount = steps.length;

  const [renderField, pagination] = useRenderField({
    onFinishForm: onFormFinished,
    form,
  });

  return (
    <AnalyticsCategoryProvider category={Categories.SURVEY}>
      <Helmet>
        <title>{session.title} - Tandemz</title>
        <meta property="og:title" content={session.title} />
        <meta property="og:description" content={session.description} />
        <meta name="description" content={session.description} />
      </Helmet>
      <Routes>
        <Route
          path="screening"
          element={
            <PublicBackground
              isLoading={isFormLoading}
              title={t('participant.publicPageTitle.applyToStudies')}
              progression={pagination.page}
              pageCount={pageCount || pagination.length}
              formContent
            >
              {renderField()}
            </PublicBackground>
          }
        />

        <Route
          path="device"
          element={
            <PublicBackground
              title={t('participant.publicPageTitle.applyToStudies')}
              progression={steps.indexOf('device')}
              pageCount={pageCount}
              formContent
            >
              <ParticipantDeviceChoice
                session={session}
                onDeviceChoiceSubmit={onDeviceChoiceSubmit}
              />
            </PublicBackground>
          }
        />

        <Route
          path="job"
          element={
            <PublicBackground
              title={t('participant.publicPageTitle.applyToStudies')}
              progression={steps.indexOf('job')}
              pageCount={pageCount}
              formContent
            >
              <ParticipantJobChoice onJobChoiceSubmit={onJobChoiceSubmit} />
            </PublicBackground>
          }
        />

        <Route
          path="profile"
          element={
            <PublicBackground
              isLoading={isFormLoading || !currentFieldsByCategories}
              title={t('participant.publicPageTitle.applyToStudies')}
              progression={steps.indexOf(currentProfileStep)}
              pageCount={pageCount}
              formContent
            >
              {currentFieldsByCategories && (
                <StudyProfile
                  criteriaRefs={criteriaRefs}
                  participantId={participantId}
                  recruitment={recruitment}
                  onSubmit={onProfileSubmit}
                  currentFieldsByCategories={currentFieldsByCategories}
                />
              )}
            </PublicBackground>
          }
        />

        <Route
          path="personal"
          element={
            <PublicBackground
              title={t('participant.publicPageTitle.applyToStudies')}
              progression={steps.indexOf('personal')}
              pageCount={pageCount}
            >
              <SignupParticipant
                onSubmit={showParticipationRules}
                participantId={participantId}
                session={session}
                countryKey={profile?.countryKey}
              />
            </PublicBackground>
          }
        />

        <Route
          path="participation-rules"
          element={
            <PublicBackground
              title={t('participant.publicPageTitle.applyToStudies')}
              progression={steps.indexOf('participation-rules')}
              pageCount={pageCount}
            >
              <ParticipationRules
                onSubmit={apply}
                hasIncentive={!!incentiveText}
              />
            </PublicBackground>
          }
        />
        <Route
          path="add-to-panel"
          element={
            participantId ? (
              <PublicBackground
                title={t('participant.publicPageTitle.applyToStudies')}
                progression={steps.indexOf('add-to-panel')}
                pageCount={pageCount}
              >
                <AddToPanel
                  participantId={participantId}
                  onSubmit={onAddToPanelSubmit}
                  countryKey={profile?.countryKey}
                />
              </PublicBackground>
            ) : (
              <Fragment />
            )
          }
        />
        <Route
          path="update-profile"
          element={
            participantId ? (
              <PublicBackground
                title={t('participant.publicPageTitle.applyToStudies')}
                progression={steps.indexOf('update-profile')}
                pageCount={pageCount}
              >
                {profile && (
                  <UpdateProfile
                    profile={profile}
                    onSubmit={goToTheFinalPage}
                    criteriaRefs={criteriaRefs}
                    participantId={participantId}
                  />
                )}
              </PublicBackground>
            ) : (
              <Fragment />
            )
          }
        />
      </Routes>
    </AnalyticsCategoryProvider>
  );
};
